import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AppSettings, defaultSettings } from "../types/settings";
import { useToast } from "@/hooks/use-toast";
import { fetchApiJson } from "../utils/api";

interface SettingsModalProps {
  open: boolean;
  onClose: () => void;
}

function clampInt(value: string, fallback: number): number {
  const n = Number.parseInt(value, 10);
  if (!Number.isFinite(n) || n < 0) return fallback;
  return n;
}

function msLabel(ms: number): string {
  if (ms === 0) return "disabled";
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.round(m / 60);
  return `${h}h`;
}

export function SettingsModal({ open, onClose }: SettingsModalProps) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<AppSettings>(defaultSettings);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const json = await fetchApiJson<{ settings?: Partial<AppSettings> }>("/api/settings");
      setSettings({ ...defaultSettings, ...(json.settings || {}) } as AppSettings);
    } catch (error) {
      toast({ title: "Settings error", description: String(error?.message || error), variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    fetchSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const canSave = useMemo(() => !loading && !saving, [loading, saving]);

  const save = async () => {
    setSaving(true);
    try {
      await fetchApiJson("/api/settings", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(settings),
      });
      toast({ title: "Saved", description: "Settings updated. New scans will use these values." });
      onClose();
    } catch (error) {
      toast({ title: "Save failed", description: String(error?.message || error), variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl bg-card border-border max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold text-foreground">Settings</DialogTitle>
          <p className="text-sm text-muted-foreground">
            These settings affect new scans. Some options may require rebuilding the scanner image to take effect.
          </p>
        </DialogHeader>

        <div className="mt-4 space-y-6">
          <section className="rounded-lg border border-border bg-muted/10 p-4">
            <h3 className="text-sm font-semibold text-foreground">Timeouts</h3>
            <p className="mt-1 text-xs text-muted-foreground">
              Global timeouts are in milliseconds. Set to 0 to disable. Idle timeouts abort only if the scanner produces no output.
            </p>

            <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label>WPScan global timeout (ms)</Label>
                <Input
                  value={String(settings.timeouts.wpscanTimeoutMs)}
                  onChange={(e) =>
                    setSettings((prev) => ({
                      ...prev,
                      timeouts: { ...prev.timeouts, wpscanTimeoutMs: clampInt(e.target.value, prev.timeouts.wpscanTimeoutMs) },
                    }))
                  }
                />
                <p className="text-xs text-muted-foreground">Current: {msLabel(settings.timeouts.wpscanTimeoutMs)}</p>
              </div>
              <div className="space-y-2">
                <Label>WPScan idle timeout (ms)</Label>
                <Input
                  value={String(settings.timeouts.wpscanIdleTimeoutMs)}
                  onChange={(e) =>
                    setSettings((prev) => ({
                      ...prev,
                      timeouts: {
                        ...prev.timeouts,
                        wpscanIdleTimeoutMs: clampInt(e.target.value, prev.timeouts.wpscanIdleTimeoutMs),
                      },
                    }))
                  }
                />
                <p className="text-xs text-muted-foreground">Current: {msLabel(settings.timeouts.wpscanIdleTimeoutMs)}</p>
              </div>

              <div className="space-y-2">
                <Label>Nuclei global timeout (ms)</Label>
                <Input
                  value={String(settings.timeouts.nucleiTimeoutMs)}
                  onChange={(e) =>
                    setSettings((prev) => ({
                      ...prev,
                      timeouts: { ...prev.timeouts, nucleiTimeoutMs: clampInt(e.target.value, prev.timeouts.nucleiTimeoutMs) },
                    }))
                  }
                />
                <p className="text-xs text-muted-foreground">Current: {msLabel(settings.timeouts.nucleiTimeoutMs)}</p>
              </div>
              <div className="space-y-2">
                <Label>Nuclei idle timeout (ms)</Label>
                <Input
                  value={String(settings.timeouts.nucleiIdleTimeoutMs)}
                  onChange={(e) =>
                    setSettings((prev) => ({
                      ...prev,
                      timeouts: {
                        ...prev.timeouts,
                        nucleiIdleTimeoutMs: clampInt(e.target.value, prev.timeouts.nucleiIdleTimeoutMs),
                      },
                    }))
                  }
                />
                <p className="text-xs text-muted-foreground">Current: {msLabel(settings.timeouts.nucleiIdleTimeoutMs)}</p>
              </div>

              <div className="space-y-2">
                <Label>httpx global timeout (ms)</Label>
                <Input
                  value={String(settings.timeouts.httpxTimeoutMs)}
                  onChange={(e) =>
                    setSettings((prev) => ({
                      ...prev,
                      timeouts: { ...prev.timeouts, httpxTimeoutMs: clampInt(e.target.value, prev.timeouts.httpxTimeoutMs) },
                    }))
                  }
                />
                <p className="text-xs text-muted-foreground">Current: {msLabel(settings.timeouts.httpxTimeoutMs)}</p>
              </div>
            </div>
          </section>

          <section className="rounded-lg border border-border bg-muted/10 p-4">
            <h3 className="text-sm font-semibold text-foreground">Scanner Options</h3>
            <div className="mt-4 space-y-4">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-sm text-foreground">Block-aware scanning</p>
                  <p className="text-xs text-muted-foreground">
                    Detects likely edge protection/rate limiting and defers WPScan/Nuclei, retrying later to avoid wasting requests.
                  </p>
                </div>
                <Switch
                  checked={settings.scanners.blockAwareScanning}
                  onCheckedChange={(checked) =>
                    setSettings((prev) => ({ ...prev, scanners: { ...prev.scanners, blockAwareScanning: checked } }))
                  }
                />
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>Block retry delay (ms)</Label>
                  <Input
                    value={String(settings.scanners.blockRetryDelayMs)}
                    onChange={(e) =>
                      setSettings((prev) => ({
                        ...prev,
                        scanners: { ...prev.scanners, blockRetryDelayMs: clampInt(e.target.value, prev.scanners.blockRetryDelayMs) },
                      }))
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label>Block retry attempts</Label>
                  <Input
                    value={String(settings.scanners.blockRetryMaxAttempts)}
                    onChange={(e) =>
                      setSettings((prev) => ({
                        ...prev,
                        scanners: {
                          ...prev.scanners,
                          blockRetryMaxAttempts: clampInt(e.target.value, prev.scanners.blockRetryMaxAttempts),
                        },
                      }))
                    }
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-2 md:grid-cols-2 md:items-center">
                <div>
                  <p className="text-sm text-foreground">WPScan profile</p>
                  <p className="text-xs text-muted-foreground">
                    Stealth is faster; Thorough expands enumeration (slower but more complete).
                  </p>
                </div>
                <div>
                  <Select
                    value={settings.scanners.wpscanProfile}
                    onValueChange={(v) =>
                      setSettings((prev) => ({
                        ...prev,
                        scanners: { ...prev.scanners, wpscanProfile: v === "thorough" ? "thorough" : "stealth" },
                      }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="stealth">stealth</SelectItem>
                      <SelectItem value="thorough">thorough</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-sm text-foreground">WPScan VulnDB enrichment</p>
                  <p className="text-xs text-muted-foreground">
                    Uses the WPScan API token to enrich results with known vulnerabilities (core/plugins/themes).
                  </p>
                </div>
                <Switch
                  checked={settings.scanners.wpscanVulnDbEnrich}
                  onCheckedChange={(checked) =>
                    setSettings((prev) => ({ ...prev, scanners: { ...prev.scanners, wpscanVulnDbEnrich: checked } }))
                  }
                />
              </div>

              <div className="space-y-2">
                <Label>WPScan API token (global)</Label>
                <Input
                  type="password"
                  placeholder="token_..."
                  value={settings.scanners.wpscanApiToken}
                  onChange={(e) =>
                    setSettings((prev) => ({ ...prev, scanners: { ...prev.scanners, wpscanApiToken: e.target.value } }))
                  }
                />
                <p className="text-xs text-muted-foreground">
                  Stored in <code>data/settings.json</code> on the API host. Prefer environment variables in production.
                </p>
              </div>

              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-sm text-foreground">Run httpx stage</p>
                  <p className="text-xs text-muted-foreground">Adds infra fingerprinting and tech/plugin hints.</p>
                </div>
                <Switch
                  checked={settings.scanners.runHttpx}
                  onCheckedChange={(checked) =>
                    setSettings((prev) => ({ ...prev, scanners: { ...prev.scanners, runHttpx: checked } }))
                  }
                />
              </div>

              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-sm text-foreground">Seed Nuclei with discovered URLs</p>
                  <p className="text-xs text-muted-foreground">
                    Uses robots/sitemaps/common WordPress paths to scan more endpoints than just the homepage.
                  </p>
                </div>
                <Switch
                  checked={settings.scanners.nucleiSeedUrls}
                  onCheckedChange={(checked) =>
                    setSettings((prev) => ({ ...prev, scanners: { ...prev.scanners, nucleiSeedUrls: checked } }))
                  }
                />
              </div>

              <div className="space-y-2">
                <Label>Nuclei seed URL limit</Label>
                <Input
                  value={String(settings.scanners.nucleiSeedMaxUrls)}
                  onChange={(e) =>
                    setSettings((prev) => ({
                      ...prev,
                      scanners: {
                        ...prev.scanners,
                        nucleiSeedMaxUrls: clampInt(e.target.value, prev.scanners.nucleiSeedMaxUrls),
                      },
                    }))
                  }
                />
              </div>

              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-sm text-foreground">Nuclei stats output</p>
                  <p className="text-xs text-muted-foreground">Shows progress/stats in live logs.</p>
                </div>
                <Switch
                  checked={settings.scanners.nucleiStats}
                  onCheckedChange={(checked) =>
                    setSettings((prev) => ({ ...prev, scanners: { ...prev.scanners, nucleiStats: checked } }))
                  }
                />
              </div>

              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-sm text-foreground">Validate Nuclei templates</p>
                  <p className="text-xs text-muted-foreground">Runs `-validate` before scans (slower).</p>
                </div>
                <Switch
                  checked={settings.scanners.nucleiValidate}
                  onCheckedChange={(checked) =>
                    setSettings((prev) => ({ ...prev, scanners: { ...prev.scanners, nucleiValidate: checked } }))
                  }
                />
              </div>

              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-sm text-foreground">Allocate TTY for WPScan</p>
                  <p className="text-xs text-muted-foreground">Can improve “live” output on some targets.</p>
                </div>
                <Switch
                  checked={settings.scanners.wpscanAllocateTty}
                  onCheckedChange={(checked) =>
                    setSettings((prev) => ({ ...prev, scanners: { ...prev.scanners, wpscanAllocateTty: checked } }))
                  }
                />
              </div>
            </div>
          </section>

          <section className="rounded-lg border border-border bg-muted/10 p-4">
            <h3 className="text-sm font-semibold text-foreground">OpenAI</h3>
            <p className="mt-1 text-xs text-muted-foreground">
              Warning: if you save an API key here it is stored in `data/settings.json` on the API host. Prefer environment variables in production.
            </p>

            <div className="mt-4 space-y-4">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-sm text-foreground">Enable OpenAI summaries</p>
                  <p className="text-xs text-muted-foreground">Used for executive summary/remediation/impact sections.</p>
                </div>
                <Switch
                  checked={settings.openai.enabled}
                  onCheckedChange={(checked) => setSettings((prev) => ({ ...prev, openai: { ...prev.openai, enabled: checked } }))}
                />
              </div>

              <div className="space-y-2">
                <Label>OpenAI API key</Label>
                <Input
                  type="password"
                  placeholder="sk-..."
                  value={settings.openai.apiKey}
                  onChange={(e) => setSettings((prev) => ({ ...prev, openai: { ...prev.openai, apiKey: e.target.value } }))}
                />
              </div>

              <div className="space-y-2">
                <Label>Model</Label>
                <Input
                  value={settings.openai.model}
                  onChange={(e) => setSettings((prev) => ({ ...prev, openai: { ...prev.openai, model: e.target.value } }))}
                />
              </div>
            </div>
          </section>

          <section className="rounded-lg border border-border bg-muted/10 p-4">
            <h3 className="text-sm font-semibold text-foreground">VPN Egress (WireGuard)</h3>
            <p className="mt-1 text-xs text-muted-foreground">
              Runs scan containers through a VPN container network namespace (e.g. Gluetun). Requires Docker capabilities
              (`/dev/net/tun`, `NET_ADMIN`) and a valid WireGuard config.
            </p>

            <div className="mt-4 space-y-4">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-sm text-foreground">Enable VPN egress</p>
                  <p className="text-xs text-muted-foreground">
                    When enabled, scans run with `--network container:&lt;name&gt;`.
                  </p>
                </div>
                <Switch
                  checked={settings.vpn.enabled}
                  onCheckedChange={(checked) => setSettings((prev) => ({ ...prev, vpn: { ...prev.vpn, enabled: checked } }))}
                />
              </div>

              <div className="space-y-2">
                <Label>VPN container name</Label>
                <Input
                  value={settings.vpn.containerName}
                  onChange={(e) =>
                    setSettings((prev) => ({ ...prev, vpn: { ...prev.vpn, containerName: e.target.value } }))
                  }
                />
              </div>

              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-sm text-foreground">Fail closed</p>
                  <p className="text-xs text-muted-foreground">
                    If the VPN container isn’t running, skip scans instead of falling back to normal egress.
                  </p>
                </div>
                <Switch
                  checked={settings.vpn.failClosed}
                  onCheckedChange={(checked) => setSettings((prev) => ({ ...prev, vpn: { ...prev.vpn, failClosed: checked } }))}
                />
              </div>
            </div>
          </section>
        </div>

        <div className="mt-6 flex items-center justify-end gap-2">
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={save} disabled={!canSave}>
            {saving ? "Saving..." : "Save"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
